import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const CATEGORIES = [
  { value: "content", label: "Official Content" },
  { value: "social", label: "Social Media" },
  { value: "professional", label: "Professional Profiles" },
  { value: "wishlist", label: "Full Wishlists" },
  { value: "other", label: "Other Links" },
];

const EMPTY = { name: "", url: "", icon: "🔗", description: "", category: "social", order: 0 };

export const LinkEditorDialog = ({ open, link, fixedCategory, onClose, onSaved }) => {
  const { token } = useAuth();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm({ ...EMPTY, ...(link || {}), category: fixedCategory || link?.category || "social" });
  }, [open, link, fixedCategory]);

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const headers = { Authorization: `Bearer ${token}` };
    const payload = { name: form.name, url: form.url, icon: form.icon || "🔗", description: form.description || null, category: form.category, order: form.order || 0 };
    try {
      if (link?.id) await axios.put(`${API}/admin/links/${link.id}`, payload, { headers });
      else await axios.post(`${API}/admin/links`, payload, { headers });
      toast.success(link?.id ? "Link updated" : "Link added");
      onSaved();
      onClose();
    } catch (error) {
      toast.error("Failed to save link");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="link-editor-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-charcoal">{link?.id ? "Edit Link" : "Add Link"}</DialogTitle>
          <DialogDescription className="font-body text-stone-grey">Saves instantly to the live site.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="font-body text-sm text-stone-grey mb-1 block">Name</Label>
            <Input data-testid="link-editor-name" value={form.name} onChange={set("name")} required className="form-input" />
          </div>
          <div>
            <Label className="font-body text-sm text-stone-grey mb-1 block">URL</Label>
            <Input data-testid="link-editor-url" type="url" value={form.url} onChange={set("url")} required placeholder="https://" className="form-input" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="font-body text-sm text-stone-grey mb-1 block">Icon</Label>
              <Input data-testid="link-editor-icon" value={form.icon} onChange={set("icon")} className="form-input" />
            </div>
            {!fixedCategory && (
              <div className="col-span-2">
                <Label className="font-body text-sm text-stone-grey mb-1 block">Category</Label>
                <select data-testid="link-editor-category" value={form.category} onChange={set("category")} className="form-input w-full">
                  {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
            )}
          </div>
          <div>
            <Label className="font-body text-sm text-stone-grey mb-1 block">Description (optional)</Label>
            <Input data-testid="link-editor-description" value={form.description || ""} onChange={set("description")} className="form-input" />
          </div>
          <div className="flex gap-2 pt-2">
            <Button type="submit" className="btn-primary" disabled={saving} data-testid="link-editor-save">{link?.id ? "Save" : "Add Link"}</Button>
            <Button type="button" variant="outline" onClick={onClose} data-testid="link-editor-cancel">Cancel</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
